import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { X, Star, Filter } from 'lucide-react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/use-toast';

interface TherapyModality {
  id: string;
  name_cn: string;
  name_en: string;
  type: string;
  evidence_level: string;
}

interface TherapyTagSelectorProps {
  value: string[];
  onChange: (value: string[]) => void;
  serviceId?: string;
}

export function TherapyTagSelector({ value, onChange }: TherapyTagSelectorProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [selectedStage, setSelectedStage] = useState<number | null>(null);
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null);
  const { t, i18n } = useTranslation('service-form');
  const { toast } = useToast();
  
  const { data: healingStages } = useQuery({
    queryKey: ['healing-stages'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('healing_stages')
        .select('stage_number, title_en, title_zh, color_theme')
        .eq('is_active', true)
        .order('stage_number');
      if (error) throw error;
      return data;
    }
  });

  const { data: effectiveDomains } = useQuery({
    queryKey: ['effective-domains'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('effective_domains')
        .select('domain_key, name_en, name_zh, icon_name')
        .eq('is_active', true)
        .order('sort_order');
      if (error) throw error;
      return data;
    }
  });

  const { data: therapies } = useQuery<TherapyModality[]>({
    queryKey: ['therapy-modalities', searchQuery, selectedStage, selectedDomain],
    queryFn: async () => {
      let query = supabase
        .from('therapy_modalities')
        .select('id, name_cn, name_en, type, evidence_level') as any;
      
      query = query.eq('is_active', true);
      
      if (searchQuery) {
        query = query.or(`name_cn.ilike.%${searchQuery}%,name_en.ilike.%${searchQuery}%`);
      }
      
      if (selectedStage !== null) {
        query = query.eq(`stage_${selectedStage}`, true);
      }
      
      if (selectedDomain) {
        query = query.eq(selectedDomain, true);
      }
      
      const { data } = await query.order('name_en');
      return (data || []) as TherapyModality[];
    }
  });
  
  const { data: selectedTherapies } = useQuery<TherapyModality[]>({
    queryKey: ['selected-therapies', value],
    queryFn: async () => {
      if (!value || value.length === 0) return [];
      const { data } = await supabase
        .from('therapy_modalities')
        .select('id, name_cn, name_en')
        .in('id', value);
      return (data || []) as TherapyModality[];
    },
    enabled: value && value.length > 0
  });
  
  const handleSelect = (therapyId: string) => {
    if (value.length >= 4) {
      toast({
        title: t('maxTherapyTags'),
        description: t('maxTherapyTagsDescription'),
        variant: "destructive"
      });
      return;
    }
    if (!value.includes(therapyId)) {
      onChange([...value, therapyId]);
    }
    setOpen(false);
    setSearchQuery('');
  };
  
  const handleRemove = (therapyId: string) => {
    onChange(value.filter(id => id !== therapyId));
  };
  
  return (
    <div className="space-y-3">
      {selectedTherapies && selectedTherapies.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedTherapies.map((therapy, index) => (
            <Badge 
              key={therapy.id} 
              variant={index === 0 ? "default" : "secondary"}
              className={`pl-3 pr-1 py-1.5 ${index === 0 ? 'bg-blue-500 hover:bg-blue-600 text-white' : ''}`}
            >
              {index === 0 && <Star className="h-3 w-3 mr-1 fill-current" />}
              <span className="text-sm">
                {i18n.language === 'zh' ? therapy.name_cn : therapy.name_en}
              </span>
              <button
                onClick={() => handleRemove(therapy.id)}
                className="ml-2 hover:bg-muted rounded-full p-0.5"
                type="button"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button 
            variant="outline" 
            className="w-full justify-start text-left" 
            type="button"
            disabled={value.length >= 4}
          >
            + {t('addTherapyTag')} ({value.length}/4)
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[500px] p-0" align="start">
          <div className="p-3 border-b space-y-3">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Filter className="h-3 w-3" />
                <span>{t('filterByStage')}</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {healingStages?.map((stage) => (
                  <Button
                    key={stage.stage_number}
                    type="button"
                    size="sm"
                    variant={selectedStage === stage.stage_number ? "default" : "outline"}
                    onClick={() => setSelectedStage(selectedStage === stage.stage_number ? null : stage.stage_number)}
                    className="text-xs h-7"
                  >
                    {i18n.language === 'zh' ? stage.title_zh : stage.title_en}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Filter className="h-3 w-3" />
                <span>{t('filterByDomain')}</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {effectiveDomains?.map((domain) => (
                  <Button
                    key={domain.domain_key}
                    type="button"
                    size="sm"
                    variant={selectedDomain === domain.domain_key ? "default" : "outline"}
                    onClick={() => setSelectedDomain(selectedDomain === domain.domain_key ? null : domain.domain_key)}
                    className="text-xs h-7"
                  >
                    {i18n.language === 'zh' ? domain.name_zh : domain.name_en}
                  </Button>
                ))}
              </div>
            </div>

            {(selectedStage !== null || selectedDomain !== null || searchQuery) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedStage(null);
                  setSelectedDomain(null);
                  setSearchQuery('');
                }}
                className="w-full text-xs"
              >
                {t('clearFilters')}
              </Button>
            )}
          </div>

          <Command>
            <CommandInput 
              placeholder={t('searchTherapy')}
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandEmpty>{t('noTherapyFound')}</CommandEmpty>
            <CommandGroup className="max-h-96 overflow-auto">
              {therapies?.map((therapy) => (
                <CommandItem
                  key={therapy.id}
                  value={therapy.name_en}
                  onSelect={() => handleSelect(therapy.id)}
                  disabled={value.includes(therapy.id)}
                  className="cursor-pointer"
                >
                  <div className="flex flex-col">
                    <span className="font-medium">
                      {therapy.name_en}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {therapy.type} · Evidence: {therapy.evidence_level}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
